import { useMutation, useQuery } from "@apollo/client/react";
import { faSoapNote } from "@awesome.me/kit-0572d5a008/icons/kit/custom";
import EmptyState from "@components/common/loading/EmptyState";
import {
  DSCardWrapper,
  DSDropdown,
} from "@components/design-system/molecules";
import {
  DSModal,
  DSNoteDisplay,
  DSTabs,
} from "@components/design-system/organisms";
import {
  faFile,
  faSpinner,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { UPDATE_AUTO_TEMPLATE } from "@graphql/mutations/AutoTemplates";
import { GET_AUTO_TEMPLATE_BY_UUID } from "@graphql/queries/AutoTemplates";
import {
  usePremiumFeature,
  useTemplates,
  useTemplateSelection,
} from "@hooks";
import { normalizeTemplateTitle } from "@src/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router";

import AutoTemplatesList from "./AutoTemplatesList";
import { useLoadUserTemplate } from "./hooks/useLoadUserTemplate";
import EmptyStateImg from "../../assets/imgs/template-empty-state.svg";
import { trackOperation } from "../../utils/operationTracking";
import PremiumFeatureGate from "../common/premium/PremiumFeatureGate";
import UpgradeModal from "../common/premium/UpgradeModal";
import { SCRIBENOTE_DEFAULTS } from "../common/record/constants";

const TemplateGroupTabPanel = ({ uuid, isActive }) => {
  const { data, loading } = useQuery(GET_AUTO_TEMPLATE_BY_UUID, {
    variables: { uuid },
    skip: !isActive,
    fetchPolicy: "cache-first",
  });

  const template = data?.autoTemplateById;

  if (loading && !template) {
    return (
      <div className="flex items-center justify-center py-xl">
        <FontAwesomeIcon
          icon={faSpinner}
          className="animate-spin text-brand text-xl"
        />
      </div>
    );
  }

  if (!template) return null;

  const fields = template.fields || [];
  const previewData =
    template.templateType === "SOAP"
      ? {
          subjective:
            fields
              .find((f) => f.fieldName === "SUBJECTIVE")
              ?.fieldValue?.trim() || undefined,
          objective:
            fields
              .find((f) => f.fieldName === "OBJECTIVE")
              ?.fieldValue?.trim() || undefined,
          assessment:
            fields
              .find((f) => f.fieldName === "ASSESSMENT")
              ?.fieldValue?.trim() || undefined,
          plan:
            fields
              .find((f) => f.fieldName === "PLAN")
              ?.fieldValue?.trim() || undefined,
        }
      : {
          content:
            fields
              .map((f) => f.fieldValue?.trim())
              .filter(Boolean)
              .join("\n\n") || undefined,
        };

  return (
    <DSNoteDisplay
      data={previewData}
      variant={template.templateType === "SOAP" ? "soap" : "freeform"}
      layout="stacked"
      size="compact"
      showCopyButton={false}
      showAdditionalNotes={false}
      dataTestId={`template-group-preview-${uuid}`}
    />
  );
};

const AutoTemplatesModal = ({
  isOpen,
  onClose,
  onTemplateSelect,
  isReprocessingMode = false,
  hideScribenoteDefaults = false,
}) => {
  const [updateAutoTemplate] = useMutation(UPDATE_AUTO_TEMPLATE);
  const navigate = useNavigate();
  const { hasFeatureAccess, UserPermissions } = usePremiumFeature();
  const {
    teamAndOrgDefaultTemplates,
    sharedTemplates,
    ownedTemplates,
    refetchTemplates: refetch,
    refetchDefaultTemplates,
  } = useTemplates();
  const { options: rawDropdownOptions, allUserTemplates } =
    useTemplateSelection({ hideScribenoteDefaults });
  const dropdownOptions = isReprocessingMode
    ? rawDropdownOptions.filter(
        (o) =>
          !o.isTemplateGroup && o.id !== "template-groups-header",
      )
    : rawDropdownOptions;
  const { releaseWebTemplateSelectionDropdown } = useFlags();
  const location = useLocation();
  const isPimsPal = location.pathname
    .toLowerCase()
    .includes("/pimspal/");

  const { loadUserTemplate, isLoadingTemplate } =
    useLoadUserTemplate();

  const [selectedTemplateKey, setSelectedTemplateKey] =
    useState(null);
  const [isDropdownActive, setIsDropdownActive] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewTemplateType, setPreviewTemplateType] =
    useState(null);
  const [previewTemplateName, setPreviewTemplateName] =
    useState(null);
  const [previewDescription, setPreviewDescription] = useState(null);
  const [previewIsDefault, setPreviewIsDefault] = useState(false);

  // Template group tabbed preview state
  const [previewGroupTemplates, setPreviewGroupTemplates] =
    useState(null);
  const [activeGroupTab, setActiveGroupTab] = useState(null);

  const refetchAllTemplates = () => {
    refetch();
    refetchDefaultTemplates();
  };

  const handleFavouriteToggle = (template) =>
    trackOperation("template-favorite-toggled", async (ctx) => {
      const newFavouriteState = !template?.isFavourite;

      ctx.addProperties({
        templateUuid: template?.uuid,
        templateOwnerUuid: template?.user?.uuid,
        isFavourited: newFavouriteState,
        source: "template selection modal",
      });

      await updateAutoTemplate({
        variables: {
          input: {
            templateUuid: template?.uuid,
            isFavourite: newFavouriteState,
          },
        },
      });

      refetchAllTemplates();
    });

  // Card grid sections (used when feature flag is off)
  const templatesToShow = ownedTemplates;

  const templateLists = [
    {
      title: "SOAPs",
      templates: templatesToShow.filter(
        (t) => t.templateType === "SOAP",
      ),
      templateType: "SOAP",
    },
    {
      title: "Freeform",
      templates: templatesToShow.filter(
        (t) => t.templateType === "CUSTOM",
      ),
      templateType: "CUSTOM",
    },
    ...(teamAndOrgDefaultTemplates.length > 0
      ? [
          {
            title: "Team Defaults",
            templates: teamAndOrgDefaultTemplates,
            templateType: "TEAM_DEFAULTS",
          },
        ]
      : []),
    ...(sharedTemplates.length > 0
      ? [
          {
            title: "Shared with you",
            templates: sharedTemplates,
            templateType: "SHARED",
          },
        ]
      : []),
    ...(!hideScribenoteDefaults
      ? [
          {
            title: "Scribenote Defaults",
            templates: SCRIBENOTE_DEFAULTS,
            templateType: "SCRIBENOTE_DEFAULT",
          },
        ]
      : []),
  ];

  const clearPreview = () => {
    setSelectedTemplateKey(null);
    setIsDropdownActive(false);
    setPreviewData(null);
    setPreviewTemplateType(null);
    setPreviewTemplateName(null);
    setPreviewDescription(null);
    setPreviewIsDefault(false);
    setPreviewGroupTemplates(null);
    setActiveGroupTab(null);
  };

  const handleDropdownSelect = async (value, option) => {
    // Ignore re-selecting the same template
    if (value === selectedTemplateKey) {
      return;
    }

    const isScribenoteDefault =
      option.templateType === "SCRIBENOTE_DEFAULT" || !option.uuid;

    // Premium gate: adaptive templates require paid access
    if (
      !isScribenoteDefault &&
      !hasFeatureAccess(UserPermissions.ADAPTIVE_TEMPLATES_ENABLED)
    ) {
      setShowUpgradeModal(true);
      return;
    }

    setSelectedTemplateKey(value);
    setIsDropdownActive(true);
    setPreviewData(null);

    // Scribenote default — no fields to load
    if (isScribenoteDefault) {
      setPreviewTemplateName(option.name);
      setPreviewDescription(option.description);
      setPreviewTemplateType(null);
      setPreviewIsDefault(true);
      return;
    }

    // Template group — show tabbed preview of each template
    if (option.isTemplateGroup) {
      setPreviewTemplateName(option.name);
      setPreviewDescription(null);
      setPreviewTemplateType(null);
      setPreviewIsDefault(false);
      setPreviewData(null);
      const groupTemplates = (option.templates || [])
        .slice()
        .sort((a, b) => a.position - b.position)
        .map((t) => t.autoTemplate)
        .filter(Boolean);
      setPreviewGroupTemplates(groupTemplates);
      setActiveGroupTab(groupTemplates[0]?.uuid ?? null);
      return;
    }

    // User/team/shared template — show metadata immediately, then load fields
    setPreviewTemplateName(option.name);
    setPreviewDescription(option.description);
    setPreviewTemplateType(option.templateType);
    setPreviewIsDefault(false);

    const result = await loadUserTemplate(option.uuid);
    if (result) {
      setPreviewTemplateType(result.templateType);
      if (result.templateType === "SOAP") {
        const fields = result.fields || [];
        const getField = (name) =>
          fields
            .find((f) => f.fieldName === name)
            ?.fieldValue?.trim() || undefined;
        setPreviewData({
          subjective: getField("SUBJECTIVE"),
          objective: getField("OBJECTIVE"),
          assessment: getField("ASSESSMENT"),
          plan: getField("PLAN"),
        });
      } else {
        const fields = result.fields || [];
        const content = fields
          .map((f) => f.fieldValue?.trim())
          .filter(Boolean)
          .join("\n\n");
        setPreviewData({ content: content || undefined });
      }
    }
  };

  const handleConfirmSelection = () => {
    if (!selectedTemplateKey) return;

    // Find the template in the dropdown options
    const option = dropdownOptions.find(
      (o) => (o.uuid || o.value) === selectedTemplateKey,
    );
    if (!option) return;

    // Template groups — pass directly, no lookup in allUserTemplates
    if (option.isTemplateGroup) {
      onTemplateSelect(option);
      handleClose();
      return;
    }

    const isScribenoteDefault =
      option.templateType === "SCRIBENOTE_DEFAULT" || !option.uuid;
    if (isScribenoteDefault) {
      onTemplateSelect(option);
      handleClose();
      return;
    }

    // For user templates, find the full template object
    const template = allUserTemplates.find(
      (t) => t.uuid === selectedTemplateKey,
    );
    if (template) {
      onTemplateSelect(template);
      handleClose();
    }
  };

  const handleClose = () => {
    clearPreview();
    onClose();
  };

  const handleCreateNewTemplate = () => {
    navigate("/templates/create");
  };

  const renderOption = (option) => {
    const icon =
      option.icon ||
      (option.templateType === "SOAP" ? faSoapNote : faFile);

    const content = (
      <div className="flex items-center min-w-0">
        <FontAwesomeIcon className="mr-2 shrink-0" icon={icon} />
        <span className="truncate">{option.name}</span>
      </div>
    );

    const isAdaptiveTemplate =
      option.templateType !== "SCRIBENOTE_DEFAULT" && option.uuid;

    if (isAdaptiveTemplate) {
      return (
        <PremiumFeatureGate
          featureName="Adaptive Templates"
          iconPosition="middle-right"
          iconSize="xs"
          className="block! w-full! relative!"
          requiredPermission={
            UserPermissions.ADAPTIVE_TEMPLATES_ENABLED
          }
        >
          {content}
        </PremiumFeatureGate>
      );
    }

    return content;
  };

  const showNewTemplateAction =
    hasFeatureAccess(UserPermissions.ADAPTIVE_TEMPLATES_ENABLED) &&
    !isPimsPal;

  const showPreview = isDropdownActive && selectedTemplateKey;

  return (
    <>
      <DSModal
        isOpen={isOpen}
        onClose={handleClose}
        title={
          isReprocessingMode ? "Change Template" : "Select Template"
        }
        subtitle={
          <>
            {isReprocessingMode ? (
              <>
                Select an option below to apply this template to the
                note.
                <div>
                  <b className="text-danger">
                    NOTE: This will overwrite any changes you have
                    made to the existing note.
                  </b>
                </div>
              </>
            ) : (
              "Select an option below to create a new note using the selected template."
            )}
          </>
        }
        actions={[
          {
            label: "Cancel",
            variant: "cancel",
            onClick: handleClose,
          },
          ...(showNewTemplateAction
            ? [
                {
                  label: "+ New Adaptive Template",
                  variant: "secondary",
                  onClick: handleCreateNewTemplate,
                },
              ]
            : []),
          ...(showPreview
            ? [
                {
                  label: isLoadingTemplate
                    ? "Loading..."
                    : isReprocessingMode
                      ? "Apply this Template"
                      : "Use this Template",
                  variant: "primary",
                  onClick: handleConfirmSelection,
                  disabled: isLoadingTemplate,
                },
              ]
            : []),
        ]}
        width="w-full max-w-[1200px]"
        dataTestId="auto-templates-modal"
      >
        <div className="bg-primary rounded-large px-lg flex flex-col h-[60vh]">
          {/* Search dropdown — fixed at top (only when flag is on) */}
          {releaseWebTemplateSelectionDropdown && (
            <div
              className="my-md flex items-center gap-sm shrink-0"
              data-testid="template-search-area"
            >
              <DSDropdown
                options={dropdownOptions}
                value={selectedTemplateKey}
                onChange={handleDropdownSelect}
                placeholder="Select a template..."
                search
                searchPlaceholder="Search templates..."
                width="w-full"
                maxHeight={400}
                dataTestId="template-search-dropdown"
                disabled={isLoadingTemplate || showUpgradeModal}
                renderOption={renderOption}
                getOptionLabel={(opt) => opt.name || opt.title || ""}
                getOptionValue={(opt) =>
                  opt.uuid || opt.value || opt.id
                }
                isOptionDisabled={(opt) => opt.isHeader}
                getOptionTestId={(opt) =>
                  opt.isHeader
                    ? `template-search-header-${opt.title}`
                    : `template-search-${opt.value || opt.uuid}`
                }
              />
              {showPreview && (
                <button
                  className="shrink-0 text-secondary hover:text-primary cursor-pointer p-xs"
                  onClick={clearPreview}
                  data-testid="template-clear-selection"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
          )}

          {/* Scrollable content area */}
          <div className="overflow-y-auto min-h-0">
            {/* Preview panel — shown when a template is selected from dropdown */}
            {showPreview && previewGroupTemplates && (
              <div className="flex flex-col gap-md px-md">
                {previewTemplateName && (
                  <h5>{previewTemplateName}</h5>
                )}
                {previewGroupTemplates.length > 0 && (
                  <DSTabs
                    value={activeGroupTab}
                    onChange={setActiveGroupTab}
                    dataTestId="template-group-tabs"
                  >
                    <DSTabs.List>
                      {previewGroupTemplates.map((t) => (
                        <DSTabs.Tab key={t.uuid} value={t.uuid}>
                          {t.name}
                        </DSTabs.Tab>
                      ))}
                    </DSTabs.List>
                    {previewGroupTemplates.map((t) => (
                      <DSTabs.Panel key={t.uuid} value={t.uuid}>
                        <TemplateGroupTabPanel
                          uuid={t.uuid}
                          isActive={activeGroupTab === t.uuid}
                        />
                      </DSTabs.Panel>
                    ))}
                  </DSTabs>
                )}
              </div>
            )}

            {showPreview && !previewGroupTemplates && (
              <div className="flex flex-col gap-md px-md">
                <div className="flex items-center gap-sm">
                  {previewTemplateName && (
                    <h5>{previewTemplateName}</h5>
                  )}
                  {previewTemplateType && (
                    <span
                      className={`inline-flex items-center px-sm py-xs rounded-full text-xs font-medium ${
                        previewTemplateType === "SOAP"
                          ? "bg-brand-subtle text-brand"
                          : "bg-blue-100 text-blue-800"
                      }`}
                    >
                      {previewTemplateType === "SOAP"
                        ? "SOAP"
                        : "Freeform"}
                    </span>
                  )}
                </div>
                {previewDescription && (
                  <p className="text-sm text-secondary">
                    {previewDescription}
                  </p>
                )}

                {isLoadingTemplate && (
                  <div className="flex items-center justify-center py-xl">
                    <FontAwesomeIcon
                      icon={faSpinner}
                      className="animate-spin text-brand text-xl"
                    />
                  </div>
                )}

                {!isLoadingTemplate && previewData && (
                  <DSNoteDisplay
                    data={previewData}
                    variant={
                      previewTemplateType === "SOAP"
                        ? "soap"
                        : "freeform"
                    }
                    layout="stacked"
                    size="compact"
                    showCopyButton={false}
                    showAdditionalNotes={false}
                    dataTestId="template-preview"
                  />
                )}

                {!isLoadingTemplate &&
                  !previewData &&
                  previewIsDefault && (
                    <div className="flex items-center justify-center py-xl text-secondary">
                      <p className="text-sm">
                        This is a default template made by Scribenote.
                        No preview available.
                      </p>
                    </div>
                  )}
              </div>
            )}

            {/* Empty state — shown when no template is selected and flag is on */}
            {!showPreview && releaseWebTemplateSelectionDropdown && (
              <DSCardWrapper variant="ghost" padding="p-xl">
                <EmptyState
                  title="Select a template above to see a preview here!"
                  img={EmptyStateImg}
                />
              </DSCardWrapper>
            )}

            {/* Card grid — shown when feature flag is off */}
            {!releaseWebTemplateSelectionDropdown && (
              <div className="flex flex-col gap-md px-md">
                {templateLists.map(
                  ({ title, templates, templateType }) => (
                    <AutoTemplatesList
                      dataTestId={`template-section-${normalizeTemplateTitle(title)}`}
                      key={templateType}
                      title={title}
                      templates={templates}
                      templateType={templateType}
                      onFavouriteToggle={handleFavouriteToggle}
                      onTemplateClick={(template) => {
                        onTemplateSelect(template);
                        handleClose();
                      }}
                      isModal={true}
                    />
                  ),
                )}
              </div>
            )}
          </div>
        </div>
      </DSModal>

      <UpgradeModal
        shouldShow={showUpgradeModal}
        setShouldShow={setShowUpgradeModal}
        featureName="Adaptive Templates"
      />
    </>
  );
};

export default AutoTemplatesModal;
